// ==========================================
// Geographic Hierarchy: Province > Division > District > Tehsil
// ==========================================
export const DIVISION = 'Multan';
export const PROVINCES = ['Punjab', 'Sindh', 'KPK', 'Balochistan', 'Gilgit-Baltistan', 'Azad Kashmir', 'ICT'];

export const PROVINCE_DIVISIONS = {
  'Punjab': ['Bahawalpur', 'Dera Ghazi Khan', 'Faisalabad', 'Gujranwala', 'Lahore', 'Multan', 'Rawalpindi', 'Sahiwal', 'Sargodha'],
  'Sindh': ['Karachi', 'Hyderabad', 'Sukkur', 'Larkana', 'Mirpur Khas', 'Shaheed Benazirabad'],
  'KPK': ['Peshawar', 'Mardan', 'Kohat', 'Bannu', 'Dera Ismail Khan', 'Hazara', 'Malakand'],
  'Balochistan': ['Quetta', 'Zhob', 'Loralai', 'Sibi', 'Nasirabad', 'Kalat', 'Makran', 'Rakhshan'],
  'Gilgit-Baltistan': ['Gilgit', 'Baltistan', 'Diamer'],
  'Azad Kashmir': ['Muzaffarabad', 'Poonch', 'Mirpur'],
  'ICT': ['Islamabad']
};

export const DIVISION_DISTRICTS = {
  'Multan': ['Vehari', 'Multan', 'Khanewal', 'Lodhran'],
  'Sahiwal': ['Sahiwal', 'Okara', 'Pakpattan'],
  'Bahawalpur': ['Bahawalpur', 'Bahawalnagar', 'Rahim Yar Khan'],
  'Dera Ghazi Khan': ['Dera Ghazi Khan', 'Layyah', 'Muzaffargarh', 'Rajanpur'],
  'Faisalabad': ['Faisalabad', 'Chiniot', 'Jhang', 'Toba Tek Singh'],
  'Gujranwala': ['Gujranwala', 'Gujrat', 'Hafizabad', 'Mandi Bahauddin', 'Narowal', 'Sialkot'],
  'Lahore': ['Lahore', 'Kasur', 'Nankana Sahib', 'Sheikhupura'],
  'Rawalpindi': ['Rawalpindi', 'Attock', 'Chakwal', 'Jhelum'],
  'Sargodha': ['Sargodha', 'Bhakkar', 'Khushab', 'Mianwali']
};

export const DISTRICTS = ['Vehari', 'Multan', 'Khanewal', 'Lodhran'];

export const DISTRICT_TEHSILS = {
  Vehari: ['Vehari', 'Mailsi', 'Burewala'],
  Multan: ['Multan City', 'Multan Saddar', 'Shujabad', 'Jalalpur Pirwala'],
  Khanewal: ['Khanewal', 'Mian Channu', 'Kabirwala', 'Jahanian'],
  Lodhran: ['Lodhran', 'Kahror Pacca', 'Dunyapur'],
};

// Helper: get divisions for a specific province
export const getDivisionsForProvince = (province) => PROVINCE_DIVISIONS[province] || [];

// Helper: get districts for a specific division
export const getDistrictsForDivision = (division) => DIVISION_DISTRICTS[division] || [];

// Helper: get tehsils for a specific district
export const getTehsilsForDistrict = (district) => DISTRICT_TEHSILS[district] || [];

// Helper: get all tehsils across all districts
export const getAllTehsils = () => Object.values(DISTRICT_TEHSILS).flat();

// Access Levels
export const ACCESS_LEVELS = {
  PROVINCE: 'province',
  DIVISION: 'division',
  DISTRICT: 'district',
  TEHSIL: 'tehsil',
};

// Map centers per district
export const DISTRICT_MAP_CONFIG = {
  Vehari: { center: [30.0452, 72.3489], zoom: 9.3 },
  Multan: { center: [30.1984, 71.4687], zoom: 10 },
  Khanewal: { center: [30.3013, 71.9322], zoom: 10 },
  Lodhran: { center: [29.5335, 71.6336], zoom: 10 },
};

// Division-level map config
export const DIVISION_MAP_CONFIG = { center: [30.0, 71.9], zoom: 8 };

// Backward-compatible exports
export const TAHSILS = DISTRICT_TEHSILS.Vehari;
export const DISTRICT = 'Vehari';

export const UNION_COUNCILS = Array.from({ length: 105 }, (_, i) => `UC-${i + 1}`);

// Default map center (Vehari, Pakistan)
export const DEFAULT_MAP_CENTER = [30.0452, 72.3489]; // Vehari district center
export const DEFAULT_MAP_ZOOM = 9.3; // Vehari district zoom level

// Dog Types
export const DOG_TYPES = {
  STRAY: 'Stray',
  PET: 'Pet'
};

export const DOG_TYPE_OPTIONS = [
  { value: DOG_TYPES.STRAY, label: 'Stray' },
  { value: DOG_TYPES.PET, label: 'Pet' }
];

// Status Types
export const STATUS_TYPES = {
  APPROVED: 'Approved',
  PENDING: 'Pending',
  REJECTED: 'Rejected'
};

export const STATUS_OPTIONS = [
  { value: STATUS_TYPES.APPROVED, label: 'Approved' },
  { value: STATUS_TYPES.PENDING, label: 'Pending' },
  { value: STATUS_TYPES.REJECTED, label: 'Rejected' }
];

// Colors
export const COLORS = {
  STRAY: '#F87171',      // Coral (soft red)
  PET: '#38BDF8',        // Sky Blue (modern)
  OTHER: '#FBBF24',      // Amber (soft)
  APPROVED: '#22C55E',   // Green
  PENDING: '#F97316',    // Orange
  REJECTED: '#EF4444',   // Red
  PRIMARY: '#3B82F6',    // Blue
  SUCCESS: '#22C55E',    // Green
  WARNING: '#F59E0B',    // Amber
  ERROR: '#EF4444'       // Red
};

// Chart Colors
export const CHART_COLORS = {
  strayDogs: COLORS.STRAY,
  petDogs: COLORS.PET,
  other: COLORS.OTHER
};

// Distribution Data Template
export const getDistributionData = (strayCount, petCount, otherCount) => [
  { name: 'Stray Dogs', value: strayCount, color: COLORS.STRAY },
  { name: 'Pet Dogs', value: petCount, color: COLORS.PET },
  { name: 'Other', value: otherCount, color: COLORS.OTHER }
].filter(item => item.value > 0);

// Time Range Options
export const TIME_RANGE_OPTIONS = [
  { value: 'This Week', label: 'This Week' },
  { value: 'This Month', label: 'This Month' },
  { value: 'Last Month', label: 'Last Month' },
  { value: 'Custom', label: 'Custom' }
];

// Pagination
export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Firebase Collection Names
export const COLLECTIONS = {
  DOG_CULLING_REPORTS: 'DogCullingReports'
};

// Menu Items (for sidebar)
export const MENU_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
  { id: 'live-map', label: 'Live Map', icon: 'Map' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
  { id: 'uc-tehsil-view', label: 'UC / Tehsil View', icon: 'MapPin' },
  { id: 'media-gallery', label: 'Media Gallery', icon: 'Image' },
  { id: 'alerts', label: 'Alerts & Notifications', icon: 'Bell' },
  { id: 'reports', label: 'Reports & Export', icon: 'FileText' },
  { id: 'users', label: 'Users & Roles', icon: 'Users' },
  { id: 'settings', label: 'System Settings', icon: 'Settings' }
];

// Months (for charts)
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Vehari District Boundary Polygon (Exact coordinates from OpenStreetMap)
// OSM Relation ID: 16117662
// Source: OpenStreetMap Contributors (polygons.openstreetmap.fr)
// Format: [latitude, longitude] for Leaflet compatibility
export const VEHARI_DISTRICT_BOUNDARY = [
  [30.010989, 71.76952],
  [30.012269184, 71.77],
  [30.025, 71.77],
  [30.025, 71.79],
  [30.01, 71.815],
  [30.01, 71.84],
  [30.025, 71.845],
  [30.045, 71.87],
  [30.04, 71.88],
  [30.03, 71.88],
  [30.05, 71.895],
  [30.045, 71.955],
  [30.05, 71.96],
  [30.07, 71.955],
  [30.095, 71.995],
  [30.09, 72.025],
  [30.075, 72.045],
  [30.075, 72.07],
  [30.085, 72.09],
  [30.07, 72.11],
  [30.12, 72.145],
  [30.125, 72.175],
  [30.155, 72.175],
  [30.165, 72.19],
  [30.165, 72.225],
  [30.18, 72.24],
  [30.185, 72.26],
  [30.18, 72.275],
  [30.19, 72.29],
  [30.195, 72.285],
  [30.205, 72.29],
  [30.215, 72.31],
  [30.211886169, 72.328682984],
  [30.2120678, 72.3298185],
  [30.2120336, 72.3298133],
  [30.211628916, 72.330226506],
  [30.205, 72.37],
  [30.225, 72.4],
  [30.205, 72.42],
  [30.2, 72.41],
  [30.185, 72.405],
  [30.185, 72.45],
  [30.165, 72.455],
  [30.165, 72.465],
  [30.205, 72.5],
  [30.265, 72.52],
  [30.275, 72.54],
  [30.27, 72.58],
  [30.235, 72.585],
  [30.235, 72.595],
  [30.25, 72.6],
  [30.255, 72.62],
  [30.28, 72.625],
  [30.33, 72.675],
  [30.33, 72.69],
  [30.31, 72.695],
  [30.3, 72.71],
  [30.31, 72.72],
  [30.34, 72.725],
  [30.33, 72.775],
  [30.35, 72.785],
  [30.355, 72.805],
  [30.37, 72.805],
  [30.375, 72.815],
  [30.37, 72.855],
  [30.315, 72.855],
  [30.315, 72.925],
  [30.295, 72.945],
  [30.295, 72.97],
  [30.305, 72.985],
  [30.3, 73],
  [30.27, 72.995],
  [30.265, 72.975],
  [30.245, 72.96],
  [30.18, 72.965],
  [30.175, 72.95],
  [30.095, 72.95],
  [30.085, 72.94],
  [30.06, 72.945],
  [30.04, 72.94],
  [30.03, 72.99],
  [30.015, 72.99],
  [30.015, 72.975],
  [29.995, 72.97],
  [29.96, 72.975],
  [29.955, 72.94],
  [29.99, 72.925],
  [29.995, 72.895],
  [29.97, 72.885],
  [29.955, 72.865],
  [29.92, 72.865],
  [29.92, 72.855],
  [29.975, 72.825],
  [29.975, 72.79],
  [29.96, 72.77],
  [29.94, 72.78],
  [29.93, 72.78],
  [29.925, 72.77],
  [29.895, 72.78],
  [29.885, 72.77],
  [29.885, 72.75],
  [29.92, 72.73],
  [29.92, 72.71],
  [29.905, 72.7],
  [29.875, 72.7],
  [29.85, 72.685],
  [29.865, 72.655],
  [29.855, 72.65],
  [29.85, 72.62],
  [29.835, 72.61],
  [29.835, 72.57],
  [29.805, 72.57],
  [29.805, 72.54],
  [29.825, 72.53],
  [29.835, 72.515],
  [29.83, 72.505],
  [29.815, 72.505],
  [29.815, 72.495],
  [29.825, 72.49],
  [29.815, 72.47],
  [29.825, 72.455],
  [29.845, 72.455],
  [29.835, 72.445],
  [29.835, 72.425],
  [29.855, 72.4],
  [29.855, 72.39],
  [29.83, 72.375],
  [29.84, 72.345],
  [29.82, 72.32],
  [29.8, 72.315],
  [29.8, 72.275],
  [29.81, 72.26],
  [29.79, 72.24],
  [29.76, 72.23],
  [29.765, 72.22],
  [29.73, 72.24],
  [29.725, 72.25],
  [29.705, 72.25],
  [29.7, 72.235],
  [29.705, 72.2],
  [29.65, 72.19],
  [29.64, 72.17],
  [29.615, 72.17],
  [29.605, 72.15],
  [29.59, 72.14],
  [29.595, 72.095],
  [29.615, 72.1],
  [29.635, 72.09],
  [29.65, 72.095],
  [29.65, 72.105],
  [29.66, 72.095],
  [29.65, 72.09],
  [29.65, 72.055],
  [29.675, 72.05],
  [29.69, 72.025],
  [29.695, 71.99],
  [29.725, 71.985],
  [29.725, 71.975],
  [29.74, 71.965],
  [29.73, 71.955],
  [29.74, 71.95],
  [29.745, 71.93],
  [29.765, 71.935],
  [29.77, 71.955],
  [29.795, 71.97],
  [29.83, 71.94],
  [29.83, 71.925],
  [29.84, 71.925],
  [29.84, 71.905],
  [29.855, 71.905],
  [29.855, 71.893914868],
  [29.854289, 71.892848],
  [29.855, 71.891730564],
  [29.855, 71.885],
  [29.88, 71.89],
  [29.885, 71.88],
  [29.905, 71.885],
  [29.90894, 71.8653],
  [29.90894, 71.86383],
  [29.909593352, 71.86203324],
  [29.915, 71.835],
  [29.895, 71.795],
  [29.915, 71.755],
  [29.93, 71.745],
  [29.93, 71.735],
  [29.95, 71.73],
  [29.965, 71.73],
  [29.965, 71.745],
  [30.005, 71.77],
  [30.010749, 71.77],
  [30.010989, 71.76952]
];

// Vehari District Boundary Style
export const VEHARI_BOUNDARY_STYLE = {
  color: '#3B82F6',      // Blue border
  weight: 3,
  opacity: 0.8,
  fillColor: '#3B82F6',
  fillOpacity: 0.1,
  dashArray: '5, 5'
};

// Multan District Boundary (OSM Relation ID: 16117648)
export const MULTAN_DISTRICT_BOUNDARY = [
  [29.608654, 71.414866],
  [29.588231, 71.37938],
  [29.569907, 71.381638],
  [29.566038, 71.364227],
  [29.522512, 71.364229],
  [29.507517, 71.386958],
  [29.493975, 71.369547],
  [29.467375, 71.392763],
  [29.422879, 71.355038],
  [29.417096, 71.31727],
  [29.459154, 71.339079],
  [29.440774, 71.297968],
  [29.447062, 71.243799],
  [29.477531, 71.23316],
  [29.482851, 71.211397],
  [29.452382, 71.139333],
  [29.47221, 71.131111],
  [29.448511, 71.126276],
  [29.450449, 71.107897],
  [29.432066, 71.100631],
  [29.442224, 71.07114],
  [29.43497, 71.047925],
  [29.450931, 71.031964],
  [29.551528, 71.00875],
  [29.551528, 71.03003],
  [29.581514, 71.026644],
  [29.605214, 71.06437],
  [29.630245, 71.0475],
  [29.674374, 71.07259],
  [29.669054, 71.119504],
  [29.685497, 71.127242],
  [29.693236, 71.181894],
  [29.718869, 71.163032],
  [29.747887, 71.180443],
  [29.789289, 71.166901],
  [29.795424, 71.18323],
  [29.828172, 71.178024],
  [29.869957, 71.19784],
  [29.888144, 71.173672],
  [29.984872, 71.215265],
  [29.977618, 71.236062],
  [29.994545, 71.276204],
  [30.023564, 71.259277],
  [30.051767, 71.266247],
  [30.073379, 71.291681],
  [30.1319, 71.285877],
  [30.16914, 71.298935],
  [30.21025, 71.32747],
  [30.227661, 71.360842],
  [30.253778, 71.376802],
  [30.27264, 71.365194],
  [30.273198, 71.392804],
  [30.300207, 71.408722],
  [30.341801, 71.412108],
  [30.388345, 71.488559],
  [30.404191, 71.489974],
  [30.422694, 71.463374],
  [30.436595, 71.503516],
  [30.425471, 71.530117],
  [30.451576, 71.528671],
  [30.454968, 71.541241],
  [30.41385, 71.577991],
  [30.42205, 71.588191],
  [30.410478, 71.612757],
  [30.36495, 71.623191],
  [30.355343, 71.652478],
  [30.364532, 71.663118],
  [30.33415, 71.652791],
  [30.31955, 71.660691],
  [30.30165, 71.707591],
  [30.31615, 71.729391],
  [30.302142, 71.746305],
  [30.306011, 71.772422],
  [30.28035, 71.777291],
  [30.268275, 71.766635],
  [30.264901, 71.820302],
  [30.242654, 71.832393],
  [30.172526, 71.792735],
  [30.134318, 71.754043],
  [30.105299, 71.752592],
  [30.045328, 71.720188],
  [30.011473, 71.621525],
  [29.978585, 71.593474],
  [29.986807, 71.564939],
  [29.958115, 71.591382],
  [29.93493, 71.557078],
  [29.899267, 71.538338],
  [29.848485, 71.546077],
  [29.872125, 71.522149],
  [29.857674, 71.513673],
  [29.861543, 71.498196],
  [29.789481, 71.508836],
  [29.812967, 71.455519],
  [29.809833, 71.434659],
  [29.749338, 71.418395],
  [29.737247, 71.427584],
  [29.744502, 71.440643],
  [29.72564, 71.445479],
  [29.702909, 71.424682],
  [29.667119, 71.432904],
  [29.608654, 71.414866],
];

// Khanewal District Boundary (OSM Relation ID: 27183458)
export const KHANEWAL_DISTRICT_BOUNDARY = [
  [30.44515, 71.560591],
  [30.41385, 71.577991],
  [30.42205, 71.588191],
  [30.410478, 71.612757],
  [30.36495, 71.623191],
  [30.355343, 71.652478],
  [30.364532, 71.663118],
  [30.33415, 71.652791],
  [30.31955, 71.660691],
  [30.30165, 71.707591],
  [30.31615, 71.729391],
  [30.302142, 71.746305],
  [30.306011, 71.772422],
  [30.28035, 71.777291],
  [30.268275, 71.766635],
  [30.264901, 71.820302],
  [30.242654, 71.832393],
  [30.172526, 71.792735],
  [30.134318, 71.754043],
  [30.105299, 71.752592],
  [30.045328, 71.720188],
  [30.011473, 71.621525],
  [29.978585, 71.593474],
  [29.986807, 71.564939],
  [29.958115, 71.591382],
  [29.969879, 71.615238],
  [29.933122, 71.607016],
  [29.913293, 71.62491],
  [29.900235, 71.672791],
  [29.902653, 71.681497],
  [29.935541, 71.67666],
  [29.95537, 71.703744],
  [29.949566, 71.736632],
  [29.963108, 71.736632],
  [29.961657, 71.750174],
  [30.00712, 71.777258],
  [30.022596, 71.773872],
  [30.005669, 71.839164],
  [30.040491, 71.873019],
  [30.023564, 71.880274],
  [30.047262, 71.898652],
  [30.040975, 71.955722],
  [30.071928, 71.963944],
  [30.072412, 71.982806],
  [30.09079, 71.999734],
  [30.069981, 72.057754],
  [30.077732, 72.08824],
  [30.063706, 72.110004],
  [30.115981, 72.149654],
  [30.116907, 72.193191],
  [30.140605, 72.178681],
  [30.157981, 72.190754],
  [30.153664, 72.210118],
  [30.177362, 72.250744],
  [30.175879, 72.273457],
  [30.207832, 72.309265],
  [30.201061, 72.366335],
  [30.217499, 72.399492],
  [30.268808, 72.406038],
  [30.289122, 72.395875],
  [30.368881, 72.440854],
  [30.432281, 72.440354],
  [30.477181, 72.478554],
  [30.540581, 72.484854],
  [30.547881, 72.503654],
  [30.593616, 72.47555],
  [30.600681, 72.449154],
  [30.563381, 72.378254],
  [30.571529, 72.366661],
  [30.558381, 72.363554],
  [30.576954, 72.354261],
  [30.559141, 72.334495],
  [30.556416, 72.306598],
  [30.526859, 72.301543],
  [30.548281, 72.301154],
  [30.548278, 72.260097],
  [30.565716, 72.244984],
  [30.551378, 72.246922],
  [30.563781, 72.213554],
  [30.54444, 72.197354],
  [30.561081, 72.197318],
  [30.577223, 72.137559],
  [30.713667, 72.202252],
  [30.730667, 72.061713],
  [30.697675, 72.031036],
  [30.69595, 71.999791],
  [30.667577, 71.999781],
  [30.660057, 71.986474],
  [30.674523, 71.962158],
  [30.662947, 71.926851],
  [30.682047, 71.900226],
  [30.68089, 71.87418],
  [30.647898, 71.832506],
  [30.648477, 71.813984],
  [30.59745, 71.773591],
  [30.575993, 71.726475],
  [30.54045, 71.706591],
  [30.50095, 71.608491],
  [30.483024, 71.605081],
  [30.44515, 71.560591],
];

// Lodhran District Boundary (OSM Relation ID: 27183370)
export const LODHRAN_DISTRICT_BOUNDARY = [
  [29.422873, 71.355021],
  [29.389972, 71.377641],
  [29.41555, 71.393329],
  [29.418961, 71.438346],
  [29.372238, 71.480635],
  [29.398839, 71.480635],
  [29.410093, 71.507577],
  [29.446244, 71.51849],
  [29.432261, 71.54475],
  [29.419302, 71.545773],
  [29.446244, 71.593519],
  [29.434648, 71.609207],
  [29.472504, 71.632397],
  [29.470116, 71.683212],
  [29.485463, 71.699582],
  [29.476937, 71.715952],
  [29.500128, 71.734368],
  [29.522978, 71.73505],
  [29.527411, 71.751761],
  [29.501492, 71.792691],
  [29.53696, 71.826454],
  [29.536979, 71.851375],
  [29.519226, 71.858507],
  [29.515134, 71.876587],
  [29.535937, 71.872835],
  [29.54685, 71.886472],
  [29.536619, 71.900795],
  [29.500469, 71.90728],
  [29.530139, 71.914101],
  [29.515475, 71.945818],
  [29.557423, 71.912055],
  [29.56629, 71.92365],
  [29.558105, 71.949569],
  [29.567311, 71.966277],
  [29.547532, 71.981622],
  [29.549238, 72.011979],
  [29.579589, 72.028006],
  [29.557081, 72.043009],
  [29.554353, 72.069274],
  [29.596301, 72.081551],
  [29.607214, 72.072684],
  [29.599409, 72.099848],
  [29.612467, 72.107586],
  [29.634715, 72.097429],
  [29.653577, 72.111455],
  [29.669537, 72.097913],
  [29.653577, 72.084371],
  [29.653577, 72.061156],
  [29.669537, 72.053418],
  [29.67921, 72.069862],
  [29.699523, 71.992962],
  [29.729509, 71.991028],
  [29.729025, 71.977002],
  [29.745469, 71.96878],
  [29.735796, 71.956206],
  [29.748371, 71.953792],
  [29.746436, 71.93493],
  [29.760946, 71.9388],
  [29.795768, 71.989093],
  [29.834459, 71.930089],
  [29.848001, 71.930573],
  [29.846067, 71.907841],
  [29.862027, 71.905907],
  [29.857674, 71.887528],
  [29.876536, 71.893332],
  [29.867347, 71.922351],
  [29.879438, 71.920416],
  [29.889111, 71.885594],
  [29.915228, 71.894783],
  [29.91813, 71.832877],
  [29.899751, 71.798538],
  [29.918613, 71.758879],
  [29.942795, 71.758396],
  [29.936508, 71.737599],
  [29.949517, 71.736156],
  [29.95537, 71.703744],
  [29.935541, 71.67666],
  [29.902653, 71.681497],
  [29.900235, 71.672791],
  [29.922966, 71.61427],
  [29.933122, 71.607016],
  [29.959723, 71.617656],
  [29.969396, 71.604597],
  [29.922482, 71.548495],
  [29.899267, 71.538338],
  [29.848485, 71.546077],
  [29.872125, 71.522149],
  [29.857674, 71.513673],
  [29.861543, 71.498196],
  [29.789481, 71.508836],
  [29.812967, 71.455519],
  [29.803023, 71.431453],
  [29.749338, 71.418395],
  [29.737247, 71.427584],
  [29.744502, 71.440643],
  [29.72564, 71.445479],
  [29.702909, 71.424682],
  [29.667119, 71.432904],
  [29.611016, 71.42033],
  [29.566038, 71.364227],
  [29.522512, 71.364229],
  [29.507517, 71.386958],
  [29.493975, 71.369547],
  [29.467375, 71.392763],
  [29.422873, 71.355021],
];

// All District Boundaries
export const DISTRICT_BOUNDARIES = {
  Vehari: VEHARI_DISTRICT_BOUNDARY,
  Multan: MULTAN_DISTRICT_BOUNDARY,
  Khanewal: KHANEWAL_DISTRICT_BOUNDARY,
  Lodhran: LODHRAN_DISTRICT_BOUNDARY,
};

// District Boundary Styles (soft pastel colors)
export const DISTRICT_BOUNDARY_STYLES = {
  Vehari: {
    color: '#059669',
    weight: 2.5,
    opacity: 0.7,
    fillColor: '#6EE7B7',
    fillOpacity: 0.12,
    dashArray: '',
  },
  Multan: {
    color: '#7C3AED',
    weight: 2.5,
    opacity: 0.7,
    fillColor: '#C4B5FD',
    fillOpacity: 0.12,
    dashArray: '',
  },
  Khanewal: {
    color: '#0284C7',
    weight: 2.5,
    opacity: 0.7,
    fillColor: '#7DD3FC',
    fillOpacity: 0.12,
    dashArray: '',
  },
  Lodhran: {
    color: '#D97706',
    weight: 2.5,
    opacity: 0.7,
    fillColor: '#FCD34D',
    fillOpacity: 0.12,
    dashArray: '',
  },
};

// Zoom threshold for switching between district and tehsil views
export const ZOOM_THRESHOLDS = {
  TEHSIL_SWITCH: 10,
};

// Vehari Tehsil Boundary (OSM Relation ID: 16108425)
// Source: OpenStreetMap Contributors
// Simplified from 495 to 100 points using Ramer-Douglas-Peucker algorithm
export const VEHARI_TEHSIL_BOUNDARY = [
  [30.087333, 72.129976],
  [30.1159813, 72.1496541],
  [30.1207813, 72.1733541],
  [30.116907, 72.193191],
  [30.128514, 72.181583],
  [30.1521971, 72.181579],
  [30.1579813, 72.1907541],
  [30.153664, 72.210118],
  [30.158984, 72.226562],
  [30.165755, 72.241071],
  [30.1744777, 72.2430532],
  [30.1821786, 72.2652567],
  [30.1758786, 72.2734567],
  [30.1860575, 72.2933022],
  [30.200093, 72.294272],
  [30.207832, 72.309265],
  [30.2120678, 72.3298185],
  [30.2015813, 72.3455541],
  [30.201061, 72.366335],
  [30.2178, 72.398825],
  [30.206661, 72.412792],
  [30.183059, 72.401822],
  [30.178738, 72.447697],
  [30.169097, 72.448361],
  [30.160454, 72.461326],
  [30.170482, 72.4830037],
  [30.1904285, 72.4893183],
  [30.2063837, 72.5046071],
  [30.197496, 72.510251],
  [30.175882, 72.501213],
  [30.181384, 72.520076],
  [30.169988, 72.509858],
  [30.171953, 72.524006],
  [30.160949, 72.533044],
  [30.138156, 72.534616],
  [30.140907, 72.5523],
  [30.124402, 72.583739],
  [30.111433, 72.581381],
  [30.10043, 72.587669],
  [30.089819, 72.577058],
  [30.074493, 72.589241],
  [30.061132, 72.577451],
  [30.043054, 72.573521],
  [30.0377131, 72.5800708],
  [30.0256668, 72.5775045],
  [30.023798, 72.605353],
  [30.004935, 72.594742],
  [29.94756, 72.584918],
  [29.956598, 72.604174],
  [29.956991, 72.62736],
  [29.934591, 72.663121],
  [29.940879, 72.709493],
  [29.922715, 72.713903],
  [29.910391, 72.694244],
  [29.886209, 72.696179],
  [29.85574, 72.683604],
  [29.870733, 72.655553],
  [29.859125, 72.643946],
  [29.854772, 72.618313],
  [29.838812, 72.604771],
  [29.839779, 72.569948],
  [29.830107, 72.56221],
  [29.808343, 72.563661],
  [29.808826, 72.542864],
  [29.830107, 72.532224],
  [29.843165, 72.512395],
  [29.8313239, 72.5026178],
  [29.817532, 72.5024577],
  [29.817532, 72.496918],
  [29.83059, 72.492565],
  [29.821401, 72.470318],
  [29.826721, 72.461129],
  [29.848485, 72.460161],
  [29.838812, 72.434045],
  [29.847592, 72.414087],
  [29.875674, 72.423249],
  [29.896222, 72.411088],
  [29.899157, 72.353217],
  [29.904609, 72.352378],
  [29.901254, 72.340636],
  [29.910061, 72.326378],
  [29.940674, 72.307507],
  [29.937738, 72.263894],
  [29.947803, 72.26641],
  [29.952416, 72.278991],
  [29.9667802, 72.2818618],
  [29.968351, 72.243345],
  [29.981351, 72.213152],
  [29.979255, 72.173313],
  [29.993093, 72.149409],
  [30.009029, 72.14228],
  [30.009029, 72.133893],
  [30.020771, 72.134313],
  [30.0279, 72.123829],
  [30.026642, 72.110409],
  [30.033771, 72.112506],
  [30.049707, 72.161151],
  [30.072352, 72.169119],
  [30.086191, 72.159474],
  [30.087333, 72.129976],
];

// Burewala Tehsil Boundary (OSM Relation ID: 16108427)
// Source: OpenStreetMap Contributors
// Simplified from 546 to 100 points using Ramer-Douglas-Peucker algorithm
export const BUREWALA_TEHSIL_BOUNDARY = [
  [29.922715, 72.713903],
  [29.92345, 72.732452],
  [29.892496, 72.751798],
  [29.892496, 72.77066],
  [29.906522, 72.77066],
  [29.912809, 72.762438],
  [29.932155, 72.773562],
  [29.961657, 72.767275],
  [29.980036, 72.787588],
  [29.981003, 72.7982275],
  [29.980519, 72.819508],
  [29.967945, 72.83837],
  [29.924417, 72.860134],
  [29.954886, 72.860134],
  [30.0013158, 72.9007595],
  [29.994061, 72.925909],
  [29.96069, 72.946222],
  [29.96069, 72.962666],
  [29.9710676, 72.9721977],
  [29.993103, 72.9671021],
  [30.0172729, 72.9724731],
  [30.0178833, 72.9821167],
  [30.0244751, 72.9844971],
  [30.033237, 72.959281],
  [30.033237, 72.94332],
  [30.0220947, 72.9346924],
  [30.024531, 72.931229],
  [30.052099, 72.942353],
  [30.0844727, 72.9326782],
  [30.0990561, 72.9462241],
  [30.178813, 72.94719],
  [30.1860962, 72.9598999],
  [30.2451172, 72.9545288],
  [30.26732, 72.971372],
  [30.276025, 72.990234],
  [30.2963257, 72.9926758],
  [30.2924805, 72.9425049],
  [30.3088989, 72.9238892],
  [30.312298, 72.852879],
  [30.3375244, 72.843689],
  [30.3701868, 72.84729],
  [30.3669797, 72.8100147],
  [30.341958, 72.810039],
  [30.328329, 72.796742],
  [30.3364023, 72.7894133],
  [30.3480359, 72.8040324],
  [30.3443784, 72.7904115],
  [30.3251011, 72.7804426],
  [30.3250975, 72.7535317],
  [30.3370612, 72.73692],
  [30.332737, 72.7252936],
  [30.31071, 72.724606],
  [30.294089, 72.712306],
  [30.304062, 72.692028],
  [30.316029, 72.691031],
  [30.3264172, 72.6784361],
  [30.311375, 72.668426],
  [30.311707, 72.658121],
  [30.2771579, 72.6305652],
  [30.25586, 72.626873],
  [30.2452236, 72.6046027],
  [30.2266862, 72.5877072],
  [30.2652398, 72.5744109],
  [30.2702233, 72.5637747],
  [30.263506, 72.553075],
  [30.2684832, 72.5411291],
  [30.2619056, 72.5245508],
  [30.235642, 72.5189016],
  [30.229599, 72.5102579],
  [30.2170272, 72.5115889],
  [30.2063837, 72.5046071],
  [30.197496, 72.510251],
  [30.175882, 72.501213],
  [30.184528, 72.516146],
  [30.181384, 72.520076],
  [30.169988, 72.509858],
  [30.171953, 72.524006],
  [30.160949, 72.533044],
  [30.138156, 72.534616],
  [30.140907, 72.5523],
  [30.129903, 72.577058],
  [30.120079, 72.584918],
  [30.111433, 72.581381],
  [30.10043, 72.587669],
  [30.089819, 72.577058],
  [30.074493, 72.589241],
  [30.061132, 72.577451],
  [30.043054, 72.573521],
  [30.0377131, 72.5800708],
  [30.0256668, 72.5775045],
  [30.023798, 72.605353],
  [30.004935, 72.594742],
  [29.978605, 72.593563],
  [29.956991, 72.58256],
  [29.94756, 72.584918],
  [29.956598, 72.604174],
  [29.956991, 72.62736],
  [29.934591, 72.663121],
  [29.940879, 72.709493],
  [29.922715, 72.713903],
];

// Mailsi Tehsil Boundary (OSM Relation ID: 16108428)
// Source: OpenStreetMap Contributors
// Simplified from 550 to 100 points using Ramer-Douglas-Peucker algorithm
export const MAILSI_TEHSIL_BOUNDARY = [
  [29.847592, 72.414087],
  [29.875674, 72.423249],
  [29.896222, 72.411088],
  [29.901254, 72.340636],
  [29.940674, 72.307507],
  [29.937738, 72.263894],
  [29.9667802, 72.2818618],
  [29.968351, 72.243345],
  [29.981351, 72.213152],
  [29.979255, 72.173313],
  [30.009029, 72.133893],
  [30.020771, 72.134313],
  [30.026642, 72.110409],
  [30.033771, 72.112506],
  [30.049707, 72.161151],
  [30.072352, 72.169119],
  [30.086191, 72.159474],
  [30.087333, 72.129976],
  [30.063706, 72.110004],
  [30.077732, 72.08824],
  [30.0699813, 72.0577541],
  [30.09079, 71.999734],
  [30.072412, 71.982806],
  [30.077732, 71.970231],
  [30.071928, 71.963944],
  [30.047746, 71.96346],
  [30.040975, 71.955722],
  [30.037589, 71.929122],
  [30.047262, 71.898652],
  [30.023564, 71.880274],
  [30.040491, 71.873019],
  [30.005669, 71.839164],
  [30.003251, 71.824655],
  [30.022596, 71.773872],
  [30.010989, 71.76952],
  [30.00712, 71.777258],
  [29.961657, 71.750174],
  [29.963108, 71.736632],
  [29.936508, 71.737599],
  [29.942795, 71.758396],
  [29.931672, 71.751625],
  [29.918613, 71.758879],
  [29.899751, 71.798538],
  [29.91813, 71.832877],
  [29.90894, 71.86383],
  [29.915228, 71.894783],
  [29.889111, 71.885594],
  [29.879438, 71.920416],
  [29.867347, 71.922351],
  [29.87702, 71.91026],
  [29.876536, 71.893332],
  [29.857674, 71.887528],
  [29.862027, 71.905907],
  [29.846067, 71.907841],
  [29.848001, 71.930573],
  [29.834459, 71.930089],
  [29.795768, 71.989093],
  [29.787062, 71.989093],
  [29.791899, 71.973617],
  [29.770618, 71.962977],
  [29.760946, 71.9388001],
  [29.746436, 71.9349301],
  [29.748371, 71.9537921],
  [29.735796, 71.956206],
  [29.745469, 71.96878],
  [29.729025, 71.977002],
  [29.729509, 71.991028],
  [29.699523, 71.992962],
  [29.67921, 72.069862],
  [29.670988, 72.068411],
  [29.669537, 72.053418],
  [29.653577, 72.061156],
  [29.653577, 72.084371],
  [29.669537, 72.097913],
  [29.653577, 72.111455],
  [29.634715, 72.097429],
  [29.612467, 72.107586],
  [29.599409, 72.099848],
  [29.593122, 72.111939],
  [29.596507, 72.137088],
  [29.618755, 72.166107],
  [29.64342, 72.164172],
  [29.65261, 72.183518],
  [29.71113, 72.196576],
  [29.703876, 72.215438],
  [29.708229, 72.244457],
  [29.721287, 72.244457],
  [29.763848, 72.217373],
  [29.767233, 72.228013],
  [29.794317, 72.2343],
  [29.810277, 72.248809],
  [29.8034989, 72.3020181],
  [29.814146, 72.299592],
  [29.803506, 72.310232],
  [29.81995, 72.31265],
  [29.843165, 72.340702],
  [29.837361, 72.372622],
  [29.857191, 72.390033],
  [29.859609, 72.402608],
  [29.847592, 72.414087],
];

// === Multan District Tehsils ===

// Multan City Tehsil Boundary (OSM Relation ID: 16117504)
export const MULTAN_CITY_TEHSIL_BOUNDARY = [
  [30.28173, 71.50076],
  [30.277253, 71.495538],
  [30.279582, 71.488552],
  [30.270267, 71.489328],
  [30.267646, 71.488498],
  [30.266385, 71.486999],
  [30.267161, 71.473026],
  [30.268714, 71.464487],
  [30.268714, 71.453213],
  [30.26796, 71.450326],
  [30.259399, 71.448185],
  [30.25086, 71.448185],
  [30.246978, 71.434988],
  [30.246984, 71.429753],
  [30.243097, 71.427226],
  [30.237655, 71.425185],
  [30.236748, 71.418127],
  [30.237214, 71.3968],
  [30.233005, 71.391517],
  [30.229124, 71.390843],
  [30.229124, 71.382202],
  [30.226019, 71.376768],
  [30.216551, 71.377544],
  [30.216703, 71.363571],
  [30.207388, 71.362018],
  [30.202542, 71.363288],
  [30.19652, 71.354255],
  [30.194967, 71.348822],
  [30.188757, 71.348822],
  [30.182547, 71.353479],
  [30.179054, 71.345716],
  [30.16935, 71.349598],
  [30.160035, 71.351927],
  [30.159775, 71.336978],
  [30.156153, 71.324757],
  [30.141404, 71.328638],
  [30.128207, 71.349598],
  [30.119668, 71.356584],
  [30.109327, 71.362888],
  [30.108024, 71.364347],
  [30.113074, 71.372315],
  [30.120445, 71.375991],
  [30.131313, 71.379096],
  [30.133641, 71.382202],
  [30.129361, 71.391362],
  [30.122773, 71.401608],
  [30.115787, 71.407042],
  [30.108024, 71.408595],
  [30.109622, 71.425594],
  [30.100261, 71.418687],
  [30.09017, 71.420239],
  [30.076197, 71.413253],
  [30.069987, 71.416358],
  [30.070763, 71.424121],
  [30.076197, 71.434212],
  [30.089393, 71.434988],
  [30.09984, 71.441432],
  [30.107248, 71.453619],
  [30.108024, 71.459053],
  [30.115787, 71.461382],
  [30.125102, 71.466816],
  [30.126655, 71.497867],
  [30.118116, 71.512616],
  [30.106472, 71.518827],
  [30.109666, 71.526239],
  [30.12355, 71.523484],
  [30.143015, 71.522152],
  [30.148646, 71.522873],
  [30.148391, 71.528142],
  [30.153825, 71.538234],
  [30.165322, 71.537732],
  [30.176337, 71.527366],
  [30.181771, 71.531247],
  [30.187088, 71.531631],
  [30.201954, 71.532023],
  [30.214538, 71.529633],
  [30.217605, 71.531178],
  [30.219032, 71.539786],
  [30.227571, 71.552983],
  [30.24232, 71.559193],
  [30.253188, 71.559193],
  [30.270267, 71.563075],
  [30.275655, 71.560009],
  [30.2757, 71.549878],
  [30.273372, 71.538234],
  [30.285792, 71.519603],
  [30.286568, 71.510288],
  [30.28173, 71.50076],
];

// Multan Saddar Tehsil Boundary (OSM Relation ID: 16117505)
export const MULTAN_SADDAR_TEHSIL_BOUNDARY = [
  [29.958115, 71.591382],
  [29.968912, 71.58235],
  [29.972781, 71.568808],
  [29.986807, 71.564939],
  [29.978585, 71.593474],
  [30.011473, 71.621525],
  [30.015825, 71.658765],
  [30.043393, 71.697941],
  [30.045328, 71.720188],
  [30.068059, 71.725508],
  [30.105299, 71.752592],
  [30.134318, 71.754043],
  [30.157533, 71.770971],
  [30.172526, 71.792735],
  [30.200577, 71.799989],
  [30.202995, 71.811597],
  [30.222825, 71.814015],
  [30.242654, 71.832393],
  [30.264901, 71.820302],
  [30.26295, 71.783991],
  [30.268275, 71.766635],
  [30.28035, 71.777291],
  [30.30065, 71.779191],
  [30.306011, 71.772422],
  [30.302142, 71.746305],
  [30.31615, 71.729391],
  [30.30165, 71.707591],
  [30.314141, 71.693647],
  [30.31955, 71.660691],
  [30.32535, 71.652991],
  [30.33415, 71.652791],
  [30.35776, 71.666985],
  [30.364532, 71.663118],
  [30.355343, 71.652478],
  [30.36495, 71.623191],
  [30.38823, 71.625878],
  [30.40385, 71.606291],
  [30.410478, 71.612757],
  [30.42205, 71.588191],
  [30.41385, 71.577991],
  [30.445248, 71.560565],
  [30.454968, 71.541241],
  [30.451576, 71.528671],
  [30.437568, 71.526645],
  [30.430791, 71.534953],
  [30.425471, 71.530117],
  [30.436595, 71.503516],
  [30.424987, 71.48804],
  [30.422694, 71.463374],
  [30.407092, 71.476432],
  [30.404191, 71.489974],
  [30.388345, 71.488559],
  [30.387747, 71.47353],
  [30.362145, 71.451683],
  [30.341801, 71.412108],
  [30.300207, 71.408722],
  [30.273198, 71.392804],
  [30.271189, 71.381155],
  [30.278105, 71.373853],
  [30.27264, 71.365194],
  [30.253778, 71.376802],
  [30.238785, 71.362292],
  [30.227661, 71.360842],
  [30.224371, 71.347496],
  [30.205897, 71.333758],
  [30.21025, 71.32747],
  [30.199126, 71.324568],
  [30.199126, 71.317797],
  [30.188002, 71.318281],
  [30.16914, 71.298935],
  [30.1585, 71.299419],
  [30.1319, 71.285877],
  [30.073379, 71.291681],
  [30.05355, 71.27572],
  [30.051767, 71.266247],
  [30.040051, 71.286287],
  [30.028975, 71.347035],
  [29.995413, 71.407112],
  [29.979974, 71.406105],
  [29.943181, 71.389035],
  [29.907479, 71.394023],
  [29.857471, 71.433291],
  [29.803506, 71.463374],
  [29.802055, 71.492392],
  [29.789964, 71.499647],
  [29.793833, 71.512705],
  [29.823336, 71.499647],
  [29.861543, 71.498196],
  [29.857674, 71.513673],
  [29.872125, 71.522149],
  [29.862027, 71.537371],
  [29.852354, 71.53592],
  [29.848485, 71.546077],
  [29.859125, 71.552364],
  [29.899267, 71.538338],
  [29.93493, 71.557078],
  [29.958115, 71.591382],
];

// Shujabad Tehsil Boundary (OSM Relation ID: 16108418)
export const SHUJABAD_TEHSIL_BOUNDARY = [
  [30.051767, 71.266247],
  [30.044079, 71.276554],
  [30.040051, 71.286287],
  [30.039801, 71.299991],
  [30.033674, 71.314144],
  [30.028975, 71.347035],
  [30.0179, 71.374557],
  [30.005969, 71.394297],
  [29.995413, 71.407112],
  [29.988365, 71.408455],
  [29.979974, 71.406105],
  [29.960843, 71.394694],
  [29.943181, 71.389035],
  [29.917548, 71.389995],
  [29.901438, 71.397379],
  [29.878951, 71.412818],
  [29.868211, 71.425236],
  [29.857471, 71.433291],
  [29.832634, 71.447723],
  [29.812967, 71.455519],
  [29.813179, 71.442577],
  [29.809833, 71.434659],
  [29.803023, 71.431453],
  [29.790931, 71.430002],
  [29.785611, 71.427584],
  [29.778357, 71.431453],
  [29.774004, 71.423715],
  [29.75756, 71.422264],
  [29.749338, 71.418395],
  [29.737247, 71.427584],
  [29.744502, 71.440643],
  [29.735313, 71.445963],
  [29.717901, 71.443544],
  [29.70581, 71.437257],
  [29.70581, 71.431937],
  [29.702909, 71.424682],
  [29.690817, 71.42565],
  [29.683563, 71.43097],
  [29.667119, 71.432904],
  [29.649708, 71.428068],
  [29.633264, 71.426133],
  [29.624075, 71.421781],
  [29.611016, 71.42033],
  [29.608654, 71.414866],
  [29.616153, 71.407887],
  [29.620466, 71.399517],
  [29.628055, 71.390885],
  [29.639958, 71.372182],
  [29.640382, 71.362832],
  [29.643358, 71.357305],
  [29.653086, 71.348388],
  [29.662485, 71.343703],
  [29.663786, 71.332637],
  [29.670987, 71.326276],
  [29.673536, 71.314801],
  [29.680338, 71.313524],
  [29.683737, 71.30885],
  [29.69394, 71.28377],
  [29.694775, 71.275686],
  [29.699464, 71.264643],
  [29.700315, 71.255292],
  [29.708391, 71.251042],
  [29.712641, 71.23744],
  [29.719868, 71.228513],
  [29.721993, 71.216612],
  [29.726668, 71.211087],
  [29.733045, 71.199185],
  [29.743902, 71.175686],
  [29.747887, 71.180443],
  [29.775939, 71.171254],
  [29.78271, 71.166901],
  [29.789289, 71.166901],
  [29.797075, 71.17751],
  [29.795424, 71.18323],
  [29.806892, 71.178508],
  [29.814751, 71.177329],
  [29.828172, 71.178024],
  [29.839048, 71.189405],
  [29.854772, 71.193017],
  [29.861335, 71.197245],
  [29.869957, 71.19784],
  [29.878471, 71.191567],
  [29.877987, 71.17609],
  [29.888144, 71.173672],
  [29.894915, 71.175606],
  [29.896366, 71.180443],
  [29.924456, 71.189357],
  [29.947269, 71.19919],
  [29.965527, 71.209461],
  [29.972781, 71.209461],
  [29.984872, 71.215265],
  [29.985356, 71.220101],
  [29.977618, 71.229774],
  [29.977618, 71.236062],
  [29.994061, 71.261695],
  [29.989225, 71.273302],
  [29.994545, 71.276204],
  [30.003251, 71.276204],
  [30.009538, 71.271368],
  [30.017276, 71.262178],
  [30.023564, 71.259277],
  [30.031621, 71.26349],
  [30.035655, 71.267498],
  [30.043877, 71.269433],
  [30.051767, 71.266247],
];

// Jalalpur Pirwala Tehsil Boundary (OSM Relation ID: 16108421)
export const JALALPUR_PIRWALA_TEHSIL_BOUNDARY = [
  [29.743902, 71.175686],
  [29.721993, 71.216612],
  [29.719868, 71.228513],
  [29.712641, 71.23744],
  [29.708391, 71.251042],
  [29.700315, 71.255292],
  [29.69394, 71.28377],
  [29.683737, 71.30885],
  [29.680338, 71.313524],
  [29.673536, 71.314801],
  [29.670987, 71.326276],
  [29.663786, 71.332637],
  [29.662485, 71.343703],
  [29.643358, 71.357305],
  [29.639958, 71.372182],
  [29.611053, 71.413838],
  [29.608654, 71.414866],
  [29.603495, 71.401647],
  [29.594089, 71.405337],
  [29.591671, 71.38454],
  [29.588231, 71.37938],
  [29.580383, 71.377397],
  [29.569907, 71.381638],
  [29.566038, 71.364227],
  [29.534463, 71.361471],
  [29.522512, 71.364229],
  [29.522512, 71.371485],
  [29.507517, 71.386958],
  [29.50268, 71.371965],
  [29.493975, 71.369547],
  [29.48527, 71.374384],
  [29.482367, 71.383089],
  [29.467375, 71.392763],
  [29.456734, 71.382122],
  [29.452383, 71.372934],
  [29.422879, 71.355038],
  [29.419494, 71.346332],
  [29.427716, 71.337143],
  [29.414174, 71.323601],
  [29.417096, 71.31727],
  [29.425298, 71.318765],
  [29.426748, 71.323117],
  [29.452726, 71.338409],
  [29.459154, 71.339079],
  [29.462538, 71.329889],
  [29.448996, 71.317316],
  [29.440774, 71.297968],
  [29.441258, 71.289746],
  [29.446094, 71.285393],
  [29.447062, 71.243799],
  [29.4548, 71.234611],
  [29.477531, 71.23316],
  [29.482851, 71.211397],
  [29.473178, 71.168835],
  [29.452382, 71.139333],
  [29.460603, 71.132562],
  [29.47221, 71.131111],
  [29.458185, 71.121923],
  [29.448511, 71.126276],
  [29.447061, 71.121438],
  [29.451898, 71.113216],
  [29.450449, 71.107897],
  [29.438356, 71.100641],
  [29.432066, 71.100631],
  [29.431101, 71.079845],
  [29.43884, 71.076943],
  [29.442224, 71.07114],
  [29.440774, 71.053728],
  [29.43497, 71.047925],
  [29.439323, 71.037285],
  [29.450931, 71.031964],
  [29.515739, 71.024226],
  [29.546208, 71.007782],
  [29.551528, 71.00875],
  [29.554914, 71.015521],
  [29.54911, 71.018422],
  [29.551528, 71.03003],
  [29.56972, 71.025758],
  [29.581514, 71.026644],
  [29.600377, 71.047441],
  [29.605214, 71.06437],
  [29.608844, 71.064368],
  [29.630245, 71.0475],
  [29.653577, 71.053245],
  [29.654061, 71.064852],
  [29.656962, 71.068721],
  [29.670021, 71.069689],
  [29.677357, 71.07813],
  [29.672439, 71.080812],
  [29.66857, 71.092903],
  [29.669054, 71.119504],
  [29.685497, 71.127242],
  [29.682842, 71.158958],
  [29.693236, 71.181894],
  [29.700974, 71.183345],
  [29.718869, 71.163032],
  [29.73415, 71.164818],
  [29.736764, 71.17077],
  [29.743902, 71.175686],
];

// === Khanewal District Tehsils ===

// Khanewal Tehsil Boundary (OSM Relation ID: 16108412)
export const KHANEWAL_TEHSIL_BOUNDARY = [
  [30.076906, 71.972182],
  [30.072412, 71.982806],
  [30.09079, 71.999734],
  [30.090306, 72.009406],
  [30.072381, 72.043254],
  [30.071444, 72.070345],
  [30.077732, 72.08824],
  [30.063706, 72.110004],
  [30.078181, 72.125954],
  [30.104103, 72.137944],
  [30.115981, 72.149654],
  [30.120781, 72.173354],
  [30.114972, 72.187871],
  [30.116907, 72.193191],
  [30.128514, 72.181583],
  [30.144896, 72.179754],
  [30.148381, 72.169854],
  [30.169881, 72.185254],
  [30.174281, 72.193054],
  [30.169581, 72.193454],
  [30.169881, 72.196654],
  [30.176981, 72.218354],
  [30.191581, 72.227554],
  [30.196881, 72.223954],
  [30.219681, 72.229154],
  [30.206081, 72.208154],
  [30.213781, 72.195654],
  [30.210481, 72.173154],
  [30.213881, 72.174854],
  [30.214881, 72.170254],
  [30.221281, 72.170654],
  [30.236281, 72.187254],
  [30.248081, 72.141254],
  [30.249307, 72.163138],
  [30.259399, 72.189532],
  [30.300581, 72.204254],
  [30.317581, 72.199654],
  [30.345581, 72.206654],
  [30.375081, 72.195754],
  [30.381275, 72.20894],
  [30.400681, 72.230654],
  [30.416381, 72.222354],
  [30.423973, 72.224452],
  [30.429381, 72.201954],
  [30.438681, 72.184054],
  [30.453481, 72.175554],
  [30.454981, 72.150754],
  [30.482181, 72.155354],
  [30.492282, 72.129236],
  [30.477295, 72.112671],
  [30.466492, 72.090881],
  [30.478078, 72.084722],
  [30.479629, 72.072314],
  [30.477156, 72.066208],
  [30.469482, 72.05072],
  [30.454102, 72.032715],
  [30.446289, 72.012512],
  [30.4315, 71.993909],
  [30.424513, 71.993909],
  [30.40435, 71.957391],
  [30.38885, 71.946591],
  [30.36865, 71.946591],
  [30.36555, 71.918591],
  [30.36005, 71.903891],
  [30.36705, 71.899991],
  [30.37015, 71.884491],
  [30.34845, 71.885191],
  [30.33005, 71.879991],
  [30.319715, 71.870481],
  [30.31665, 71.862691],
  [30.30345, 71.858091],
  [30.29875, 71.858791],
  [30.29335, 71.865791],
  [30.28785, 71.865091],
  [30.28165, 71.830891],
  [30.283225, 71.822378],
  [30.27855, 71.819991],
  [30.264736, 71.818092],
  [30.242654, 71.832393],
  [30.222825, 71.814015],
  [30.216703, 71.813655],
  [30.21748, 71.838654],
  [30.212046, 71.844864],
  [30.192639, 71.843311],
  [30.192639, 71.869705],
  [30.1841, 71.860389],
  [30.164693, 71.859613],
  [30.161587, 71.881349],
  [30.167798, 71.892217],
  [30.172455, 71.892217],
  [30.177889, 71.903861],
  [30.165469, 71.90619],
  [30.16314, 71.9124],
  [30.170903, 71.919387],
  [30.16935, 71.93336],
  [30.140628, 71.927926],
  [30.135194, 71.931031],
  [30.136747, 71.950438],
  [30.122773, 71.947333],
  [30.104143, 71.938017],
  [30.097933, 71.940346],
  [30.095604, 71.965963],
  [30.088617, 71.972174],
  [30.076906, 71.972182],
];

// Mian Channu Tehsil Boundary (OSM Relation ID: 16108424)
export const MIAN_CHANNU_TEHSIL_BOUNDARY = [
  [30.570972, 72.164043],
  [30.55595, 72.156891],
  [30.531921, 72.167908],
  [30.491696, 72.129264],
  [30.482181, 72.155354],
  [30.454981, 72.150754],
  [30.453481, 72.175554],
  [30.438681, 72.184054],
  [30.423973, 72.224452],
  [30.416381, 72.222354],
  [30.400681, 72.230654],
  [30.375081, 72.195754],
  [30.345581, 72.206654],
  [30.317581, 72.199654],
  [30.300581, 72.204254],
  [30.259399, 72.189532],
  [30.248081, 72.141254],
  [30.236281, 72.187254],
  [30.221281, 72.170654],
  [30.210481, 72.173154],
  [30.213781, 72.195654],
  [30.206081, 72.208154],
  [30.219681, 72.229154],
  [30.188081, 72.225954],
  [30.176981, 72.218354],
  [30.169881, 72.185254],
  [30.148381, 72.169854],
  [30.144896, 72.179754],
  [30.157981, 72.190754],
  [30.153664, 72.210118],
  [30.165755, 72.241071],
  [30.174478, 72.243053],
  [30.177362, 72.250744],
  [30.182179, 72.265257],
  [30.175879, 72.273457],
  [30.186058, 72.293302],
  [30.200093, 72.294272],
  [30.207832, 72.309265],
  [30.212068, 72.329819],
  [30.201581, 72.345554],
  [30.201061, 72.366335],
  [30.218986, 72.400723],
  [30.268808, 72.406038],
  [30.289122, 72.395875],
  [30.304603, 72.407484],
  [30.317654, 72.407],
  [30.337481, 72.426354],
  [30.368881, 72.440854],
  [30.432281, 72.440354],
  [30.446751, 72.447103],
  [30.477181, 72.478554],
  [30.503433, 72.487285],
  [30.525581, 72.480954],
  [30.540581, 72.484854],
  [30.540581, 72.496454],
  [30.547881, 72.503654],
  [30.567909, 72.496974],
  [30.579843, 72.480953],
  [30.593616, 72.47555],
  [30.600681, 72.449154],
  [30.589381, 72.434854],
  [30.583881, 72.406554],
  [30.563381, 72.378254],
  [30.571529, 72.366661],
  [30.558381, 72.363554],
  [30.576954, 72.354261],
  [30.571916, 72.342248],
  [30.559141, 72.334495],
  [30.565716, 72.32791],
  [30.556416, 72.306598],
  [30.526859, 72.301543],
  [30.548281, 72.301154],
  [30.550681, 72.295654],
  [30.548278, 72.260097],
  [30.565716, 72.244984],
  [30.551378, 72.246922],
  [30.564166, 72.229484],
  [30.559509, 72.221278],
  [30.563781, 72.213554],
  [30.54444, 72.197354],
  [30.561081, 72.197318],
  [30.570972, 72.164043],
];

// Kabirwala Tehsil Boundary (OSM Relation ID: 16108364)
export const KABIRWALA_TEHSIL_BOUNDARY = [
  [30.673562, 71.865128],
  [30.68089, 71.87418],
  [30.682047, 71.900226],
  [30.67105, 71.91296],
  [30.665262, 71.915275],
  [30.662947, 71.926851],
  [30.673944, 71.946531],
  [30.674523, 71.962158],
  [30.664683, 71.972577],
  [30.660057, 71.986474],
  [30.667577, 71.999781],
  [30.69595, 71.999791],
  [30.697675, 72.031036],
  [30.730667, 72.061713],
  [30.723722, 72.092969],
  [30.72715, 72.123091],
  [30.72545, 72.143891],
  [30.71175, 72.182591],
  [30.713667, 72.202252],
  [30.613056, 72.147515],
  [30.577223, 72.137559],
  [30.57095, 72.164091],
  [30.56445, 72.156891],
  [30.55595, 72.156891],
  [30.541321, 72.166321],
  [30.531921, 72.167908],
  [30.477295, 72.112671],
  [30.466492, 72.090881],
  [30.478078, 72.084722],
  [30.479629, 72.072314],
  [30.469482, 72.05072],
  [30.454102, 72.032715],
  [30.446289, 72.012512],
  [30.4315, 71.993909],
  [30.424513, 71.993909],
  [30.40435, 71.957391],
  [30.38885, 71.946591],
  [30.36865, 71.946591],
  [30.36555, 71.918591],
  [30.36005, 71.903891],
  [30.36705, 71.899991],
  [30.37015, 71.884491],
  [30.34845, 71.885191],
  [30.33005, 71.879991],
  [30.319715, 71.870481],
  [30.31665, 71.862691],
  [30.30345, 71.858091],
  [30.29875, 71.858791],
  [30.29335, 71.865791],
  [30.28785, 71.865091],
  [30.28165, 71.830891],
  [30.283225, 71.822378],
  [30.27455, 71.818191],
  [30.26505, 71.818291],
  [30.26355, 71.774891],
  [30.268275, 71.766635],
  [30.28035, 71.777291],
  [30.30065, 71.779191],
  [30.306011, 71.772422],
  [30.307449, 71.760297],
  [30.302142, 71.746305],
  [30.30845, 71.733691],
  [30.31615, 71.729391],
  [30.302142, 71.713901],
  [30.30165, 71.707591],
  [30.30745, 71.697891],
  [30.314141, 71.693647],
  [30.31955, 71.660691],
  [30.32535, 71.652991],
  [30.33415, 71.652791],
  [30.34565, 71.659291],
  [30.35385, 71.660191],
  [30.35776, 71.666985],
  [30.364532, 71.663118],
  [30.355343, 71.652478],
  [30.36495, 71.623191],
  [30.38823, 71.625878],
  [30.391616, 71.616689],
  [30.40385, 71.606291],
  [30.410478, 71.607983],
  [30.410478, 71.612757],
  [30.42205, 71.588191],
  [30.41675, 71.585291],
  [30.41385, 71.577991],
  [30.44515, 71.560591],
  [30.453039, 71.57171],
  [30.462228, 71.578481],
  [30.46945, 71.590591],
  [30.477221, 71.594441],
  [30.483024, 71.605081],
  [30.50095, 71.608491],
  [30.50965, 71.648091],
  [30.53065, 71.674791],
  [30.54045, 71.706591],
  [30.568629, 71.726475],
  [30.575993, 71.726475],
  [30.574968, 71.72832],
  [30.584229, 71.736424],
  [30.59465, 71.769991],
  [30.60705, 71.782791],
  [30.631112, 71.79141],
  [30.648477, 71.813984],
  [30.647898, 71.832506],
  [30.660053, 71.840609],
  [30.673562, 71.865128],
];

// Jahanian Tehsil Boundary (OSM Relation ID: 16108414)
export const JAHANIAN_TEHSIL_BOUNDARY = [
  [30.076906, 71.972182],
  [30.088617, 71.972174],
  [30.095604, 71.965963],
  [30.097933, 71.940346],
  [30.104143, 71.938017],
  [30.122773, 71.947333],
  [30.136747, 71.950438],
  [30.135194, 71.931031],
  [30.140628, 71.927926],
  [30.16935, 71.93336],
  [30.170903, 71.919387],
  [30.16314, 71.9124],
  [30.165469, 71.90619],
  [30.177889, 71.903861],
  [30.172455, 71.892217],
  [30.167798, 71.892217],
  [30.161587, 71.881349],
  [30.164693, 71.859613],
  [30.1841, 71.860389],
  [30.192639, 71.869705],
  [30.192639, 71.843311],
  [30.212046, 71.844864],
  [30.21748, 71.838654],
  [30.216703, 71.813655],
  [30.202995, 71.811597],
  [30.200577, 71.799989],
  [30.183649, 71.798055],
  [30.172526, 71.792735],
  [30.16382, 71.784996],
  [30.157533, 71.770971],
  [30.134318, 71.754043],
  [30.124161, 71.750174],
  [30.114005, 71.753559],
  [30.105299, 71.752592],
  [30.079666, 71.736632],
  [30.068059, 71.725508],
  [30.045328, 71.720188],
  [30.046779, 71.71245],
  [30.041942, 71.705195],
  [30.044844, 71.703261],
  [30.043393, 71.697941],
  [30.032269, 71.686333],
  [30.031302, 71.674242],
  [30.022113, 71.662151],
  [30.015825, 71.658765],
  [30.010505, 71.641838],
  [30.011473, 71.621525],
  [29.999865, 71.616205],
  [30.001316, 71.60895],
  [29.993094, 71.606048],
  [29.978585, 71.593474],
  [29.988258, 71.573644],
  [29.986807, 71.564939],
  [29.97665, 71.565422],
  [29.969396, 71.575095],
  [29.968912, 71.58235],
  [29.958115, 71.591382],
  [29.969396, 71.604597],
  [29.969879, 71.615238],
  [29.949083, 71.616205],
  [29.944246, 71.609434],
  [29.933122, 71.607016],
  [29.922966, 71.61427],
  [29.913293, 71.62491],
  [29.906522, 71.641838],
  [29.907006, 71.654413],
  [29.900235, 71.672791],
  [29.902653, 71.681497],
  [29.910391, 71.681497],
  [29.916195, 71.67666],
  [29.921031, 71.682948],
  [29.928286, 71.674726],
  [29.935541, 71.67666],
  [29.945214, 71.691653],
  [29.947632, 71.704228],
  [29.95537, 71.703744],
  [29.95537, 71.710031],
  [29.947632, 71.71777],
  [29.949566, 71.736632],
  [29.963108, 71.736632],
  [29.961657, 71.750174],
  [29.97665, 71.755494],
  [30.00712, 71.777258],
  [30.010989, 71.76952],
  [30.022596, 71.773872],
  [30.021145, 71.786931],
  [30.003251, 71.824655],
  [30.005669, 71.839164],
  [30.011473, 71.845935],
  [30.030818, 71.857543],
  [30.040491, 71.873019],
  [30.029367, 71.873019],
  [30.023564, 71.880274],
  [30.030818, 71.889947],
  [30.047262, 71.898652],
  [30.046779, 71.907358],
  [30.037589, 71.929122],
  [30.040975, 71.955722],
  [30.047746, 71.96346],
  [30.061771, 71.959591],
  [30.071928, 71.963944],
  [30.076906, 71.972182],
];

// === Lodhran District Tehsils ===

// Lodhran Tehsil Boundary (OSM Relation ID: 16108405)
export const LODHRAN_TEHSIL_BOUNDARY = [
  [29.684794, 71.673705],
  [29.683669, 71.702569],
  [29.66396, 71.729827],
  [29.642992, 71.731924],
  [29.624121, 71.722698],
  [29.589314, 71.721859],
  [29.583024, 71.727311],
  [29.580088, 71.772187],
  [29.564991, 71.790634],
  [29.545701, 71.797768],
  [29.529766, 71.788537],
  [29.501622, 71.789834],
  [29.527411, 71.751761],
  [29.522978, 71.73505],
  [29.500128, 71.734368],
  [29.489897, 71.720727],
  [29.476937, 71.715952],
  [29.485463, 71.699582],
  [29.470116, 71.683212],
  [29.478983, 71.659681],
  [29.472504, 71.632397],
  [29.463637, 71.618756],
  [29.438059, 71.615687],
  [29.434648, 71.609207],
  [29.446244, 71.593519],
  [29.426804, 71.571351],
  [29.419302, 71.545773],
  [29.432261, 71.54475],
  [29.446244, 71.51849],
  [29.438059, 71.510987],
  [29.410093, 71.507577],
  [29.398839, 71.480635],
  [29.386903, 71.485751],
  [29.372238, 71.480635],
  [29.387926, 71.459832],
  [29.407706, 71.452329],
  [29.418961, 71.438346],
  [29.41555, 71.393329],
  [29.389972, 71.377641],
  [29.394065, 71.372184],
  [29.408729, 71.369797],
  [29.422873, 71.355021],
  [29.452383, 71.372934],
  [29.467375, 71.392763],
  [29.493975, 71.369547],
  [29.50268, 71.371965],
  [29.507517, 71.386958],
  [29.522512, 71.371485],
  [29.522512, 71.364229],
  [29.557708, 71.362188],
  [29.566038, 71.364227],
  [29.569907, 71.381638],
  [29.588231, 71.37938],
  [29.594089, 71.405337],
  [29.603495, 71.401647],
  [29.611016, 71.42033],
  [29.667119, 71.432904],
  [29.702909, 71.424682],
  [29.70581, 71.437257],
  [29.72564, 71.445479],
  [29.744502, 71.440643],
  [29.737247, 71.427584],
  [29.749338, 71.418395],
  [29.774004, 71.423715],
  [29.778357, 71.431453],
  [29.785611, 71.427584],
  [29.811432, 71.436026],
  [29.812967, 71.455519],
  [29.800604, 71.46821],
  [29.802055, 71.492392],
  [29.79335, 71.494327],
  [29.789692, 71.509024],
  [29.742799, 71.532729],
  [29.740283, 71.544891],
  [29.744476, 71.564181],
  [29.755379, 71.570891],
  [29.749508, 71.595633],
  [29.765863, 71.611149],
  [29.767541, 71.648472],
  [29.757896, 71.64931],
  [29.743218, 71.634213],
  [29.718057, 71.638826],
  [29.697508, 71.651407],
  [29.684794, 71.673705],
];

// Kahror Pacca Tehsil Boundary (OSM Relation ID: 16108361)
export const KAHROR_PACCA_TEHSIL_BOUNDARY = [
  [29.599409, 72.099848],
  [29.612467, 72.107586],
  [29.634715, 72.097429],
  [29.653577, 72.111455],
  [29.662283, 72.098397],
  [29.669537, 72.097913],
  [29.653577, 72.084371],
  [29.653577, 72.061156],
  [29.669537, 72.053418],
  [29.674374, 72.056803],
  [29.670988, 72.068411],
  [29.67921, 72.069862],
  [29.699523, 71.992962],
  [29.729509, 71.991028],
  [29.729025, 71.977002],
  [29.745469, 71.96878],
  [29.735796, 71.956206],
  [29.748371, 71.953792],
  [29.746436, 71.93493],
  [29.761518, 71.939881],
  [29.773412, 71.89715],
  [29.757057, 71.86486],
  [29.756218, 71.835505],
  [29.734412, 71.841376],
  [29.723928, 71.827956],
  [29.718895, 71.807827],
  [29.740283, 71.80615],
  [29.745315, 71.79944],
  [29.731895, 71.754574],
  [29.736508, 71.73444],
  [29.731895, 71.722698],
  [29.712605, 71.70802],
  [29.705895, 71.684117],
  [29.697508, 71.67573],
  [29.684794, 71.673705],
  [29.683669, 71.702569],
  [29.66396, 71.729827],
  [29.642992, 71.731924],
  [29.624121, 71.722698],
  [29.589314, 71.721859],
  [29.583024, 71.727311],
  [29.580088, 71.772187],
  [29.557443, 71.795671],
  [29.545701, 71.797768],
  [29.529766, 71.788537],
  [29.501622, 71.789834],
  [29.507972, 71.802922],
  [29.528434, 71.813494],
  [29.53696, 71.826454],
  [29.536979, 71.851375],
  [29.519226, 71.858507],
  [29.515134, 71.876587],
  [29.535937, 71.872835],
  [29.54685, 71.886472],
  [29.536619, 71.900795],
  [29.501833, 71.901819],
  [29.500469, 71.90728],
  [29.504902, 71.912737],
  [29.530139, 71.914101],
  [29.514793, 71.933535],
  [29.515475, 71.945818],
  [29.528093, 71.943089],
  [29.544463, 71.919558],
  [29.557423, 71.912055],
  [29.56629, 71.92365],
  [29.565949, 71.939333],
  [29.558105, 71.949569],
  [29.567311, 71.966277],
  [29.547532, 71.981622],
  [29.549238, 72.011979],
  [29.574816, 72.020505],
  [29.579589, 72.028006],
  [29.576862, 72.034824],
  [29.557081, 72.043009],
  [29.554353, 72.069274],
  [29.561515, 72.078482],
  [29.572087, 72.075413],
  [29.596301, 72.081551],
  [29.600052, 72.073025],
  [29.607214, 72.072684],
  [29.599409, 72.099848],
];

// Dunyapur Tehsil Boundary (OSM Relation ID: 16108407)
export const DUNYAPUR_TEHSIL_BOUNDARY = [
  [29.958115, 71.591382],
  [29.969396, 71.604597],
  [29.969879, 71.615238],
  [29.949083, 71.616205],
  [29.933122, 71.607016],
  [29.922966, 71.61427],
  [29.906522, 71.641838],
  [29.900235, 71.672791],
  [29.902653, 71.681497],
  [29.916195, 71.67666],
  [29.921031, 71.682948],
  [29.928286, 71.674726],
  [29.935541, 71.67666],
  [29.947632, 71.704228],
  [29.95537, 71.703744],
  [29.947632, 71.71777],
  [29.949517, 71.736156],
  [29.936508, 71.737599],
  [29.936508, 71.754527],
  [29.942795, 71.758396],
  [29.935541, 71.76033],
  [29.931672, 71.751625],
  [29.918613, 71.758879],
  [29.899751, 71.798538],
  [29.91813, 71.832877],
  [29.90894, 71.86383],
  [29.915228, 71.894783],
  [29.889111, 71.885594],
  [29.879438, 71.920416],
  [29.867347, 71.922351],
  [29.87702, 71.91026],
  [29.876536, 71.893332],
  [29.857674, 71.887528],
  [29.854289, 71.892848],
  [29.862027, 71.905907],
  [29.846067, 71.907841],
  [29.848001, 71.930573],
  [29.834459, 71.930089],
  [29.830107, 71.946049],
  [29.804473, 71.965878],
  [29.795768, 71.989093],
  [29.787062, 71.989093],
  [29.791899, 71.973617],
  [29.770618, 71.962977],
  [29.761518, 71.939876],
  [29.773412, 71.89715],
  [29.757057, 71.86486],
  [29.756218, 71.835505],
  [29.734412, 71.841376],
  [29.720153, 71.816219],
  [29.718895, 71.807827],
  [29.740283, 71.80615],
  [29.745315, 71.79944],
  [29.731895, 71.754574],
  [29.73525, 71.72773],
  [29.712605, 71.70802],
  [29.705895, 71.684117],
  [29.684794, 71.673705],
  [29.697508, 71.651407],
  [29.718057, 71.638826],
  [29.743218, 71.634213],
  [29.757896, 71.64931],
  [29.767541, 71.648472],
  [29.765863, 71.611149],
  [29.749508, 71.595633],
  [29.755379, 71.570891],
  [29.740702, 71.556632],
  [29.742799, 71.532729],
  [29.789692, 71.509024],
  [29.806892, 71.510287],
  [29.823336, 71.499647],
  [29.861543, 71.498196],
  [29.857674, 71.513673],
  [29.872125, 71.522149],
  [29.862027, 71.537371],
  [29.852354, 71.53592],
  [29.848485, 71.546077],
  [29.859125, 71.552364],
  [29.899267, 71.538338],
  [29.93493, 71.557078],
  [29.958115, 71.591382],
];

// Tehsil Boundary Styles (colored fills)
export const TEHSIL_BOUNDARY_STYLES = {
  // Vehari District Tehsils
  Vehari: {
    color: '#059669',
    weight: 1,
    opacity: 0.5,
    fillColor: '#6EE7B7', // Soft emerald
    fillOpacity: 0.18,
    dashArray: '',
  },
  Burewala: {
    color: '#0284C7',
    weight: 1,
    opacity: 0.5,
    fillColor: '#7DD3FC', // Soft sky blue
    fillOpacity: 0.18,
    dashArray: '',
  },
  Mailsi: {
    color: '#D97706',
    weight: 1,
    opacity: 0.5,
    fillColor: '#FCD34D', // Soft amber
    fillOpacity: 0.18,
    dashArray: '',
  },
  // Multan District Tehsils
  'Multan City': {
    color: '#7C3AED',
    weight: 1,
    opacity: 0.5,
    fillColor: '#C4B5FD', // Soft violet
    fillOpacity: 0.18,
    dashArray: '',
  },
  'Multan Saddar': {
    color: '#DB2777',
    weight: 1,
    opacity: 0.5,
    fillColor: '#F9A8D4', // Soft pink
    fillOpacity: 0.18,
    dashArray: '',
  },
  Shujabad: {
    color: '#0891B2',
    weight: 1,
    opacity: 0.5,
    fillColor: '#67E8F9', // Soft cyan
    fillOpacity: 0.18,
    dashArray: '',
  },
  'Jalalpur Pirwala': {
    color: '#CA8A04',
    weight: 1,
    opacity: 0.5,
    fillColor: '#FDE68A', // Soft yellow
    fillOpacity: 0.18,
    dashArray: '',
  },
  // Khanewal District Tehsils
  Khanewal: {
    color: '#2563EB',
    weight: 1,
    opacity: 0.5,
    fillColor: '#93C5FD', // Soft blue
    fillOpacity: 0.18,
    dashArray: '',
  },
  'Mian Channu': {
    color: '#059669',
    weight: 1,
    opacity: 0.5,
    fillColor: '#A7F3D0', // Soft mint
    fillOpacity: 0.18,
    dashArray: '',
  },
  Kabirwala: {
    color: '#E11D48',
    weight: 1,
    opacity: 0.5,
    fillColor: '#FDA4AF', // Soft rose
    fillOpacity: 0.18,
    dashArray: '',
  },
  Jahanian: {
    color: '#9333EA',
    weight: 1,
    opacity: 0.5,
    fillColor: '#D8B4FE', // Soft purple
    fillOpacity: 0.18,
    dashArray: '',
  },
  // Lodhran District Tehsils
  Lodhran: {
    color: '#EA580C',
    weight: 1,
    opacity: 0.5,
    fillColor: '#FDBA74', // Soft orange
    fillOpacity: 0.18,
    dashArray: '',
  },
  'Kahror Pacca': {
    color: '#0D9488',
    weight: 1,
    opacity: 0.5,
    fillColor: '#5EEAD4', // Soft teal
    fillOpacity: 0.18,
    dashArray: '',
  },
  Dunyapur: {
    color: '#4F46E5',
    weight: 1,
    opacity: 0.5,
    fillColor: '#A5B4FC', // Soft indigo
    fillOpacity: 0.18,
    dashArray: '',
  },
};

// Tehsil Boundaries organized by district
export const TEHSIL_BOUNDARIES_BY_DISTRICT = {
  Vehari: {
    Vehari: VEHARI_TEHSIL_BOUNDARY,
    Burewala: BUREWALA_TEHSIL_BOUNDARY,
    Mailsi: MAILSI_TEHSIL_BOUNDARY,
  },
  Multan: {
    'Multan City': MULTAN_CITY_TEHSIL_BOUNDARY,
    'Multan Saddar': MULTAN_SADDAR_TEHSIL_BOUNDARY,
    Shujabad: SHUJABAD_TEHSIL_BOUNDARY,
    'Jalalpur Pirwala': JALALPUR_PIRWALA_TEHSIL_BOUNDARY,
  },
  Khanewal: {
    Khanewal: KHANEWAL_TEHSIL_BOUNDARY,
    'Mian Channu': MIAN_CHANNU_TEHSIL_BOUNDARY,
    Kabirwala: KABIRWALA_TEHSIL_BOUNDARY,
    Jahanian: JAHANIAN_TEHSIL_BOUNDARY,
  },
  Lodhran: {
    Lodhran: LODHRAN_TEHSIL_BOUNDARY,
    'Kahror Pacca': KAHROR_PACCA_TEHSIL_BOUNDARY,
    Dunyapur: DUNYAPUR_TEHSIL_BOUNDARY,
  },
};

// Flat map of all tehsil boundaries (backward compatible)
export const TEHSIL_BOUNDARIES = Object.values(TEHSIL_BOUNDARIES_BY_DISTRICT)
  .reduce((acc, districtTehsils) => ({ ...acc, ...districtTehsils }), {});
